import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Pause, Loader2, Lock, Send, Music, Flame, Gem, Disc, Leaf, Sparkles, Film, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PRESETS = [
  { id: 'universal',  name: 'Universal',   color: '#A855F7', Icon: Music },
  { id: 'fuego',      name: 'Fuego',       color: '#EF4444', Icon: Flame },
  { id: 'claridad',   name: 'Claridad',    color: '#3B82F6', Icon: Gem },
  { id: 'cinta',      name: 'Cinta',       color: '#F97316', Icon: Disc },
  { id: 'natural',    name: 'Natural',     color: '#22C55E', Icon: Leaf },
  { id: 'espacial',   name: 'Espacial',    color: '#06B6D4', Icon: Sparkles },
  { id: 'cinematico', name: 'Cinemático',  color: '#FACC15', Icon: Film },
  { id: 'empuje',     name: 'Empuje',      color: '#EC4899', Icon: Zap },
];

/**
 * BandLab-style mastering player.
 * Top: big play button + filename + master/original toggle + waveform progress.
 * Bottom: 4x2 grid of preset cards with colorful icons; click → instant switch.
 *
 * Props:
 *  - track, mode, jobId, freePreset, hasMonthly, isStaff, onPublished, onUpgrade, onRefresh
 */
export default function TrackVariantsPlayer({
  track, mode = 'admin', jobId, freePreset = 'natural',
  hasMonthly = false, isStaff = false,
  onPublished, onUpgrade, onRefresh,
}) {
  const audioRef = useRef(null);
  const [activePreset, setActivePreset] = useState(track.preset_id || freePreset);
  const [activeUrl, setActiveUrl] = useState('');
  const [showOriginal, setShowOriginal] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [busyPreset, setBusyPreset] = useState(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [meta, setMeta] = useState({
    title: track.publish_meta?.title || '',
    genre: track.publish_meta?.genre || '',
    price: track.publish_meta?.price || 5.99,
  });
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const variants = track.variants || {};

  // Resolve URL for active preset (or original)
  useEffect(() => {
    if (showOriginal) { setActiveUrl(track.source_url || ''); return; }
    const v = variants[activePreset];
    if (v?.url && v?.status === 'completed') setActiveUrl(v.url);
    else if (activePreset === track.preset_id) setActiveUrl(track.mastered_url || '');
    else setActiveUrl('');
  }, [activePreset, showOriginal, JSON.stringify(variants), track.mastered_url, track.source_url, track.preset_id]); // eslint-disable-line

  // Re-bind audio src when activeUrl changes (preserve play+time)
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !activeUrl) return;
    const wasPlaying = !a.paused && !a.ended;
    const t = a.currentTime;
    a.src = activeUrl;
    a.load();
    a.currentTime = t || 0;
    if (wasPlaying) a.play().catch(() => {});
  }, [activeUrl]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a || !activeUrl) return;
    if (a.paused) { a.play().catch(() => {}); }
    else { a.pause(); }
  };

  // Wire audio events to keep playing/progress state in sync
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPauseEv = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setProgress(0); };
    const onTime = () => setProgress(a.currentTime || 0);
    const onMeta = () => setDuration(a.duration || 0);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPauseEv);
    a.addEventListener('ended', onEnded);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPauseEv);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
    };
  }, []);

  const handlePresetClick = async (presetId) => {
    setShowOriginal(false);
    const v = variants[presetId];
    if (v?.status === 'completed' && v?.url) { setActivePreset(presetId); return; }
    if (v?.status === 'processing') { setActivePreset(presetId); return; }
    if (presetId === track.preset_id) { setActivePreset(presetId); return; }

    if (mode === 'customer' && !isStaff && !hasMonthly && presetId !== freePreset) {
      if (onUpgrade) onUpgrade(presetId);
      else toast.error('Preset premium. Compra un pack para desbloquear los 8 presets.');
      return;
    }

    setBusyPreset(presetId);
    try {
      const r = await axios.post(`${API_URL}/api/mastering-og/jobs/${jobId}/tracks/${track.id}/preview-variant?preset=${presetId}`, {}, { withCredentials: true });
      if (r.data?.status === 'completed' && r.data?.url) {
        setActivePreset(presetId);
      } else {
        setActivePreset(presetId);
        toast.info(`Generando "${PRESETS.find(p => p.id === presetId)?.name}"…`);
        let tries = 0;
        const poll = setInterval(async () => {
          tries += 1;
          if (tries > 30) { clearInterval(poll); return; }
          if (onRefresh) await onRefresh();
        }, 2000);
        setTimeout(() => clearInterval(poll), 70000);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        if (onUpgrade) onUpgrade(presetId);
        else toast.error(e.response.data.detail || 'Preset premium');
      } else { toast.error(e?.response?.data?.detail || 'Error generando preset'); }
    } finally { setBusyPreset(null); }
  };

  const handlePublish = async (overrides = {}) => {
    setPublishing(true);
    try {
      await axios.post(`${API_URL}/api/mastering-og/admin-bulk/${jobId}/tracks/${track.id}/publish`,
        {
          title: overrides.title ?? meta.title,
          genre: overrides.genre ?? meta.genre,
          price: overrides.price ?? meta.price,
          chosen_preset: activePreset,
        },
        { withCredentials: true });
      toast.success(`Publicado con ${PRESETS.find(p => p.id === activePreset)?.name}`);
      setPublishOpen(false);
      if (onPublished) onPublished();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error publicando'); }
    finally { setPublishing(false); }
  };

  const isPublished = !!track.published_track_id;
  const presetStatus = (presetId) => {
    if (presetId === track.preset_id && track.status === 'completed') return 'completed';
    return variants[presetId]?.status || 'idle';
  };
  const fmtTime = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`;

  const activeColor = PRESETS.find(p => p.id === activePreset)?.color || '#E8720C';

  return (
    <div className="bg-gradient-to-br from-black/60 to-black/40 border border-white/10 rounded-2xl p-5 mb-4" data-testid={`bandlab-row-${track.id}`}>
      {/* Header: play + filename + original/mastered toggle */}
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={togglePlay}
          disabled={!activeUrl}
          className="w-14 h-14 rounded-full bg-white/10 hover:bg-white/15 disabled:opacity-30 flex items-center justify-center flex-shrink-0 transition"
          data-testid={`play-${track.id}`}
        >
          {playing ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 ml-0.5 text-white" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold truncate" style={{ color: 'white' }}>{track.filename}</p>
          <p className="text-xs text-white/40 mt-0.5">
            {fmtTime(progress)} / {fmtTime(duration || track.duration_sec || 0)}
          </p>
        </div>
        {track.status === 'completed' && (
          <div className="flex items-center gap-1 bg-black/50 border border-white/10 rounded-full p-1">
            <button
              onClick={() => setShowOriginal(true)}
              className={`px-3 py-1 text-[11px] font-bold rounded-full transition ${showOriginal ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
              data-testid={`orig-btn-${track.id}`}
            >Original</button>
            <button
              onClick={() => setShowOriginal(false)}
              className={`px-3 py-1 text-[11px] font-bold rounded-full transition ${!showOriginal ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
              data-testid={`mast-btn-${track.id}`}
            >Masterizado</button>
          </div>
        )}
        {isPublished && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-green-500/20 text-green-300 font-bold">✓ Publicado</span>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-5" onClick={(e) => {
        const a = audioRef.current; if (!a || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pct = (e.clientX - rect.left) / rect.width;
        a.currentTime = pct * duration;
      }} role="progressbar" aria-valuenow={progress} style={{ cursor: 'pointer' }}>
        <div className="h-full transition-all" style={{ width: `${duration ? (progress/duration)*100 : 0}%`, background: activeColor }} />
      </div>

      <audio ref={audioRef} preload="metadata" />

      {/* 4x2 preset card grid — BandLab style */}
      {track.status === 'completed' && !isPublished && (
        <>
          <div className="grid grid-cols-4 gap-2" data-testid={`presets-${track.id}`}>
            {PRESETS.map(p => {
              const st = presetStatus(p.id);
              const isActive = activePreset === p.id && !showOriginal;
              const locked = mode === 'customer' && !isStaff && !hasMonthly && p.id !== freePreset && p.id !== track.preset_id && st !== 'completed';
              const ready = st === 'completed';
              return (
                <button
                  key={p.id}
                  onClick={() => handlePresetClick(p.id)}
                  disabled={busyPreset === p.id}
                  className={`relative h-[110px] rounded-lg border flex flex-col items-center justify-center gap-1 transition group ${
                    isActive ? 'bg-white/[0.04]' : 'bg-white/[0.02] hover:bg-white/[0.04] border-white/8'
                  }`}
                  style={{
                    borderColor: isActive ? p.color : undefined,
                    borderWidth: isActive ? '2px' : '1px',
                    boxShadow: isActive ? `0 0 0 1px ${p.color}30, 0 4px 16px ${p.color}15` : undefined,
                  }}
                  data-testid={`preset-${track.id}-${p.id}`}
                >
                  {locked && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/60 flex items-center justify-center">
                      <Lock className="w-2.5 h-2.5 text-white/70" />
                    </span>
                  )}
                  {busyPreset === p.id || st === 'processing' ? (
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: p.color }} />
                  ) : (
                    <p.Icon className="w-7 h-7" style={{ color: ready ? p.color : `${p.color}70`, strokeWidth: 1.5 }} />
                  )}
                  <span className={`text-[11px] font-semibold ${isActive ? 'text-white' : 'text-white/80'}`}>{p.name}</span>
                </button>
              );
            })}
          </div>

          {/* Publish controls (admin only) */}
          {mode === 'admin' && (
            <div className="mt-5 pt-4 border-t border-white/5 flex justify-end">
              <button
                onClick={() => setPublishOpen(true)}
                className="px-5 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition"
                style={{ background: activeColor, color: '#fff' }}
                data-testid={`pub-btn-${track.id}`}
              >
                <Send className="w-3.5 h-3.5" /> Publicar con {PRESETS.find(p => p.id === activePreset)?.name}
              </button>
            </div>
          )}

          {/* Customer: download chosen preset */}
          {mode === 'customer' && activeUrl && !showOriginal && (
            <div className="mt-5 pt-4 border-t border-white/5 flex justify-end">
              <a
                href={activeUrl}
                download={`${(track.publish_meta?.title || track.filename || 'master').replace(/\.[^.]+$/, '')}_${activePreset}.wav`}
                className="px-5 py-2 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition"
                style={{ background: activeColor, color: '#fff' }}
                data-testid={`dl-active-${track.id}`}
              >
                Descargar con {PRESETS.find(p => p.id === activePreset)?.name}
              </a>
            </div>
          )}
        </>
      )}

      {/* Publish dialog (asks meta only when user actually publishes) */}
      {publishOpen && (
        <PublishDialog
          track={track}
          activePresetName={PRESETS.find(p => p.id === activePreset)?.name || activePreset}
          activeColor={activeColor}
          initial={meta}
          publishing={publishing}
          onCancel={() => setPublishOpen(false)}
          onConfirm={(data) => { setMeta(data); handlePublish(data); }}
        />
      )}

      {track.status === 'processing' && (
        <div className="flex items-center gap-2 text-sm text-blue-300 py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Masterizando con {PRESETS.find(p => p.id === track.preset_id)?.name}…
        </div>
      )}

      {track.status === 'failed' && (
        <p className="text-sm text-red-400 py-2">⚠ {track.error || 'Error masterizando'}</p>
      )}
    </div>
  );
}

const GENRE_OPTIONS = ['Trap', 'Reggaeton', 'Hip-Hop', 'Pop', 'R&B', 'Latin', 'Afrobeat', 'Drill', 'House', 'EDM', 'Rock', 'Indie', 'Otro'];

function PublishDialog({ track, activePresetName, activeColor, initial, publishing, onCancel, onConfirm }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [genre, setGenre] = useState(initial?.genre || '');
  const [price, setPrice] = useState(initial?.price || 5.99);

  const valid = title.trim() && genre && price > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onCancel} data-testid="publish-dialog">
      <div className="bg-[#0f0f10] border border-white/10 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: activeColor }}>Publicar al catálogo</p>
          <h3 className="text-xl font-bold text-white">{track.filename}</h3>
          <p className="text-xs text-white/40 mt-1">Se publicará con preset <strong style={{ color: activeColor }}>{activePresetName}</strong></p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold block mb-1.5">Título</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full input-glass text-sm"
              placeholder="Nombre de la canción"
              data-testid="dialog-title-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold block mb-1.5">Género</label>
            <select
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="w-full input-glass text-sm"
              data-testid="dialog-genre-select"
            >
              <option value="">— Selecciona un género —</option>
              {GENRE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold block mb-1.5">Precio (USD)</label>
            <input
              type="number" step="0.01" min="0"
              value={price}
              onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
              className="w-full input-glass text-sm"
              placeholder="5.99"
              data-testid="dialog-price-input"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={onCancel}
            disabled={publishing}
            className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium"
            data-testid="dialog-cancel-btn"
          >Cancelar</button>
          <button
            onClick={() => onConfirm({ title: title.trim(), genre, price })}
            disabled={!valid || publishing}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ background: activeColor, color: '#fff' }}
            data-testid="dialog-confirm-btn"
          >
            {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> Publicando…</> : <>Publicar al catálogo</>}
          </button>
        </div>
      </div>
    </div>
  );
}
